// Shared HUD look.
//
// The old chrome was mono, uppercase, wide-tracked and cold blue-black: it
// read as a security console, which is exactly the wrong first impression for
// a small bird you've come to visit. Everything here is the opposite of that,
// so panels stay consistent as they get reworked: warm near-black instead of
// blue, soft cream text, generous rounding, sentence case, no letter-spacing
// theatrics.
//
// Inline styles rather than Tailwind classes because most of the HUD already
// styles that way, and these values need to be readable from JS (canvas
// overlays, dynamic opacity).

export const ui = {
  // Surfaces. Warm charcoal with a hint of brown, never blue.
  panel: 'rgba(28, 23, 21, 0.62)',
  panelSolid: 'rgba(28, 23, 21, 0.92)',
  panelLift: 'rgba(46, 38, 34, 0.7)',
  border: '1px solid rgba(255, 238, 222, 0.16)',
  borderStrong: '1px solid rgba(255, 238, 222, 0.3)',
  blur: 'blur(10px)',

  // Ink.
  text: 'rgba(255, 249, 242, 0.92)',
  textSoft: 'rgba(255, 249, 242, 0.62)',
  textFaint: 'rgba(255, 249, 242, 0.38)',

  // Warmth. Amber is the house accent; blush is for anything affectionate.
  accent: '#f0b064',
  blush: '#f0a0a0',
  good: '#a8d6a0',

  // Shape.
  radius: 14,
  radiusPill: 999,
  shadow: '0 10px 34px rgba(0, 0, 0, 0.42)',

  // Type. Rounded face, sentence case, normal tracking.
  font: 'var(--font-rounded), ui-rounded, "SF Pro Rounded", system-ui, sans-serif',
  size: { xs: 11, sm: 12.5, md: 14, lg: 17 },
}

// ── Kawaii game-UI kit ──────────────────────────────────────────────────
//
// The glassy dark panels above were a warm-up; this is the actual look. Cream
// card, heavy dark outline, dashed stitch line inside it like a sticker, and a
// solid offset shadow instead of a soft blur. Reads as a friendly game HUD
// rather than a monitoring tool, and it holds up over both the bright daytime
// sky and deep night, which glass panels do not.
export const kawaii = {
  cream: '#f7e7d2',
  creamDeep: '#ecd6ba',   // bar troughs, pressed states
  ink: '#2f2650',         // outlines and text: warm navy, never pure black
  inkSoft: 'rgba(47, 38, 80, 0.62)',
  night: '#3b2f6b',       // dark pills (the clock)

  outline: '2.5px solid #2f2650',
  outlineThin: '2px solid #2f2650',
  stitch: '1.5px dashed rgba(47, 38, 80, 0.34)',

  radius: 26,
  radiusPill: 999,
  // Solid offset shadow. The whole card sits ON the world rather than
  // floating in it, which is what makes game UI feel tactile.
  drop: '0 4px 0 rgba(47, 38, 80, 0.32)',
  dropSm: '0 3px 0 rgba(47, 38, 80, 0.3)',
}

/** A cream card with the outline, the stitch line and the drop shadow. */
export function card({ radius = kawaii.radius, pad = '10px 14px' } = {}) {
  return {
    background: kawaii.cream,
    border: kawaii.outline,
    borderRadius: radius,
    boxShadow: kawaii.drop,
    color: kawaii.ink,
    fontFamily: ui.font,
    padding: pad,
  }
}

/** The dashed stitch line, drawn as an inset pseudo-border child. */
export function stitchInset(radius = kawaii.radius) {
  return {
    position: 'absolute',
    inset: 4,
    border: kawaii.stitch,
    borderRadius: Math.max(4, radius - 5),
    pointerEvents: 'none',
  }
}


// ── The dark room ───────────────────────────────────────────────────────
//
// Black border on dark grey, purple for signal only: the place-actions
// popup and anything else that should feel like a back room rather than a
// sticker. Lived inline in ActionsHud until more surfaces wanted it.
export const ink = {
  bg: '#211d29',          // the panel
  bgDeep: '#17141e',      // wells inside it
  border: '#000000',
  stitch: '#2e2938',
  line: '#7c5cff',
  soft: '#a78bfa',
  text: '#e9e4f7',
  dim: '#8d84a8',
  danger: '#ff7b9c',
  good: '#4ade80',
}
